import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../../../services/api';
import { useNotification } from '../../../contexts/NotificationContext';
import styles from '../../../styles/TaskDetails.module.css';

interface TaskInstance {
  id: string;
  status: string;
  base_points?: number;
}

interface TemplateStatisticsProps {
  templateId: string;
  groupId?: string | null;
  taskId: string;
}

interface Statistics {
  total: number;
  in_progress: number;
  completed: number;
  points_distributed: number;
}

const TemplateStatistics: React.FC<TemplateStatisticsProps> = ({ templateId, groupId, taskId }) => {
  const [statistics, setStatistics] = useState<Statistics>({
    total: 0,
    in_progress: 0,
    completed: 0,
    points_distributed: 0
  });
  const [loading, setLoading] = useState(true);
  const notificationService = useNotification();
  const navigate = useNavigate();

  useEffect(() => {
    loadStatistics();
  }, [templateId, groupId]);

  const loadStatistics = async () => {
    setLoading(true);
    try {
      // Use the new template statistics API
      const response = await apiService.getTemplateStatistics(templateId);

      if (response.status === 'success' && response.data) {
        const statsData = response.data.statistics;
        const stats: Statistics = {
          total: statsData.total_instances || 0,
          in_progress: statsData.by_status?.in_progress || 0,
          completed: statsData.by_status?.completed || 0,
          points_distributed: statsData.points_distributed || 0
        };

        setStatistics(stats);
      } else {
        notificationService.error(response.message || 'Failed to load template statistics');
      }
    } catch (error: any) {
      console.error('Error loading statistics:', error);
      notificationService.error(error.response?.data?.message || error.response?.data?.error || 'Failed to load template statistics');
    } finally {
      setLoading(false);
    }
  };

  const handleNavigation = (tab: string) => {
    if (groupId) {
      navigate(`/group-management/groups/${groupId}/tasks/${taskId}/${tab}`);
    } else {
      navigate(`/task-management/${taskId}/${tab}`);
    }
  };

  if (loading) {
    return (
      <div className={styles.templateStatisticsSection}>
        <div className={styles.statisticsGrid}>
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className={styles.statisticCardSkeleton}>
              <div className={styles.skeletonStatisticValue}></div>
              <div className={styles.skeletonStatisticLabel}></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.templateStatisticsSection}>
      <div className={styles.statisticsGrid}>
        <div className={styles.statisticCard}>
          <div className={styles.statisticValue}>{statistics.total}</div>
          <div className={styles.statisticLabel}>TOTAL INSTANCES CREATED</div>
        </div>

        <div className={styles.statisticCard}>
          <div className={styles.statisticValue}>{statistics.in_progress}</div>
          <div className={styles.statisticLabel}>IN PROGRESS</div>
        </div>

        <div className={styles.statisticCard}>
          <div className={styles.statisticValue}>{statistics.completed}</div>
          <div className={styles.statisticLabel}>COMPLETED</div>
        </div>

        <div className={styles.statisticCard}>
          <div className={styles.statisticValue}>{statistics.points_distributed}</div>
          <div className={styles.statisticLabel}>POINTS DISTRIBUTED</div>
        </div>
      </div>

    </div>
  );
};

export default TemplateStatistics;
